import { apiGet, apiPost } from './index'

export interface YtCreditsStatus {
  station_id: number
  station_name: string
  allowance: number
  used: number
  remaining: number | null
  window_started_at: string
  resets_at: string
  interval_days: number
  default_bitrate: number
  bitrate_options: number[]
  max_file_size_mb: number
}

export interface ConvertRequest {
  station_id: number
  url: string
  bitrate: number
  duration_sec?: number | null
}

export interface ConversionResult {
  attachment_id: number
  video_id: string
  bitrate: number
  title?: string
  duration_sec?: number | null
  filesize_bytes?: number | null
  source_url?: string
  cached: boolean
  file_url?: string | null
  edit_url?: string | null
}

export interface CreditReservation {
  station_id: number
  station_name: string
  allowance: number
  used: number
  remaining: number | null
  period_start_utc: string
  resets_at: string
  interval_days: number
}

export interface QuotaReservation {
  station_id: number
  user_id: number
  max_per_day: number
  count_per_day: number
  max_per_second: number
  period_start_utc: string
  resets_at: string
  burst?: {
    bucket: string | null
    count: number
  }
}

export interface ConvertResponse {
  status: 'success'
  conversion: ConversionResult
  credits: CreditReservation
  quota: QuotaReservation
}

export function getCreditsStatus(stationId: number): Promise<YtCreditsStatus> {
  const params = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<YtCreditsStatus>(`ytmp3/credits/status?${params.toString()}`)
}

export function convertSingleVideo(payload: ConvertRequest): Promise<ConvertResponse> {
  return apiPost<ConvertResponse>('ytmp3/convert', payload)
}

// Settings
export type YtInterval = 'weekly' | 'monthly' | 'custom'

export interface YtSettingsResponse {
  provider: {
    rapidapi_username: string
    rapidapi_key: string
    default_bitrate: number
    retention_days: number
  }
  credits: {
    defaults: {
      allowance: number
      interval: YtInterval
      custom_days: number
    }
    stations: Array<{
      station_id: number
      name: string
      allowance: number
      interval: YtInterval
      custom_days: number
      uses_default: boolean
    }>
    timezone: string
  }
  quotas: {
    max_per_day: number
    max_per_second: number
    warning_threshold: number
  }
  file_limits: {
    max_mb: number
  }
  access: {
    superadmin_only: boolean
  }
}

type YtSettingsEnvelope = {
  settings: YtSettingsResponse
}

export function getYtSettings(): Promise<YtSettingsResponse> {
  return apiGet<YtSettingsEnvelope>('ytmp3/settings').then((res) => res.settings)
}

export function updateYtSettings(payload: YtSettingsResponse): Promise<{ success: boolean; settings: YtSettingsResponse }> {
  return apiPost<{ success: boolean; settings: YtSettingsResponse }>('ytmp3/settings', payload)
}

// Playlist
export interface PlaylistItemSummary {
  video_id: string
  title?: string
  duration_sec?: number | null
  estimated_size_bytes?: number | null
  over_limit?: boolean
  thumb_url?: string | null
}

export interface PlaylistFetchResponse {
  station_id: number
  playlist: {
    title?: string
    url: string
    id?: string
    item_count?: number
    thumb_url?: string | null
  }
  items: PlaylistItemSummary[]
}

export interface PlaylistStartResponse {
  job_id: string
  queued: number
}

export type PlaylistItemStatus = 'pending' | 'processing' | 'success' | 'error' | 'skipped'

export interface PlaylistJobItem {
  video_id: string
  title?: string
  status: PlaylistItemStatus
  message?: string
  file_url?: string | null
  edit_url?: string | null
  attachment_id?: number | null
  cached?: boolean
}

export interface PlaylistJobStatus {
  job_id: string
  playlist_title?: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  items: PlaylistJobItem[]
  zip_url?: string | null
  error?: string | null
}

export function fetchPlaylist(payload: {
  station_id: number
  url: string
  bitrate?: number
}): Promise<PlaylistFetchResponse> {
  return apiPost<PlaylistFetchResponse>('ytmp3/playlist/fetch', payload)
}

export interface PlaylistStartRequest {
  station_id: number
  url: string
  playlist_id?: string
  playlist_title?: string
  bitrate?: number
  items: Array<{
    video_id: string
    duration_sec?: number | null
    estimated_size_bytes?: number | null
  }>
}

export function startPlaylistJob(payload: PlaylistStartRequest): Promise<PlaylistStartResponse> {
  return apiPost<PlaylistStartResponse>('ytmp3/playlist/start', payload)
}

export function pollPlaylistJob(stationId: number, jobId: string): Promise<PlaylistJobStatus> {
  const params = new URLSearchParams({
    station_id: String(stationId),
    job_id: jobId,
  })
  return apiGet<PlaylistJobStatus>(`ytmp3/playlist/poll?${params.toString()}`)
}

export interface PlaylistHistoryJobItem {
  video_id: string
  title?: string
  status: PlaylistItemStatus
  message?: string | null
  file_url?: string | null
  attachment_id?: number | null
  cached?: boolean
}

export interface PlaylistHistoryJob {
  job_id: number
  playlist_id?: string
  playlist_title?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  zip_url?: string | null
  error?: string | null
  created_at?: string | null
  items: PlaylistHistoryJobItem[]
}

export interface PlaylistHistoryResponse {
  items: PlaylistHistoryJob[]
  next_cursor?: number | null
}

export function getPlaylistHistory(
  stationId: number,
  cursor?: number | null,
  limit?: number,
): Promise<PlaylistHistoryResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  if (cursor) {
    params.set('cursor', String(cursor))
  }

  if (limit) {
    params.set('limit', String(limit))
  }

  return apiGet<PlaylistHistoryResponse>(`ytmp3/history/playlists?${params.toString()}`, { cache: 'no-store' })
}

// Credits admin
export interface CreditsUsageResponse {
  stations: Array<{
    station_id: number
    station_name: string
    allowance: number
    used: number
    remaining: number | null
    window_started_at: string
    resets_at: string
  }>
  ledger: Array<{
    station_id: number
    station_name: string
    user_id: number
    video_id: string
    type: 'debit' | 'topup' | 'adjustment'
    amount: number
    meta: Record<string, unknown>
    created_at?: string
  }>
}

export function getCreditsUsage(): Promise<CreditsUsageResponse> {
  return apiGet<CreditsUsageResponse>('ytmp3/credits/usage')
}

export function postCreditTopUp(payload: { station_id: number; amount: number; note?: string }): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('ytmp3/credits/topup', payload)
}

export interface HistoryEntry {
  id: number
  video_id: string
  title?: string
  outcome: string
  created_at?: string | null
  duration_sec?: number | null
  actual_size_bytes?: number | null
  estimated_size_bytes?: number | null
  bitrate?: number | null
  file_url?: string | null
  user?: {
    id: number
    name: string
  } | null
}

export interface HistoryResponse {
  items: HistoryEntry[]
  next_cursor?: number | null
}

export function getHistory(stationId: number, cursor?: number | null, limit?: number): Promise<HistoryResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  if (cursor) {
    params.set('cursor', String(cursor))
  }

  if (limit) {
    params.set('limit', String(limit))
  }

  return apiGet<HistoryResponse>(`ytmp3/history?${params.toString()}`, { cache: 'no-store' })
}

